// 1:1 wrappers around the Withings webservice interaction-time endpoints
// (programinteraction / programinteractiontime) — the same scalews services
// wpc-front's auto-timer + billing surfaces use.

import { ws } from '@/lib/wiservice';
import { getProgramId } from '@/config/program';

export interface CptInteraction {
  id?: string | number;
  code: string | number;
  status?: number;
  value?: string | number;
  remaining?: number;
  threshold?: number;
}

export interface CptMonthBucket {
  date: string;
  interactions?: CptInteraction[];
}

interface ListInteractionsBody {
  program_interactions?: CptMonthBucket[];
}

/** Monthly CPT interaction buckets (per-month `date` + interactions with seconds in `value`). */
export async function listCptInteractions(
  userid: string | number,
  window: { startdateymd: string; enddateymd: string },
): Promise<CptMonthBucket[]> {
  const response = await ws<ListInteractionsBody>('programinteraction', 'list', {
    programid: getProgramId(),
    userid: String(userid),
    startdateymd: window.startdateymd,
    enddateymd: window.enddateymd,
  });
  return response.body.program_interactions ?? [];
}

export interface InteractionTimeEntry {
  id: string | number;
  time: string | number;
  date?: string;
  created?: string | number;
  manual?: boolean | number;
  author?: { id?: string | number; firstname?: string };
}

interface ListTimesBody {
  interaction_times?: InteractionTimeEntry[];
}

/** Individual time entries (auto + manual) for a patient. */
export async function listTimeEntries(userid: string | number): Promise<InteractionTimeEntry[]> {
  const response = await ws<ListTimesBody>('programinteractiontime', 'list', {
    programid: getProgramId(),
    userid: String(userid),
  });
  return response.body.interaction_times ?? [];
}

export interface CreateTimeEntryParams {
  userid: string | number;
  /** Whole-minute seconds. The backend derives `manual` from the absence of auto_start_date. */
  timeSeconds: number;
  dateYmd: string;
  /** REQUIRED by scalews (503 Missing params without it) — resolve via logic/timeTracking resolveAuthorId. */
  authorid?: string | number;
  /** Unix seconds of the auto session start — present ONLY on auto-timer flushes. */
  autoStartDateUnix?: number;
  note?: string;
  /** Snapshot from the timer store; read endpoints resolve getProgramId() live instead. */
  programid?: string;
}

export async function createTimeEntry(p: CreateTimeEntryParams): Promise<void> {
  await ws('programinteractiontime', 'create', {
    programid: p.programid ?? getProgramId(),
    userid: String(p.userid),
    date: p.dateYmd,
    time: p.timeSeconds,
    ...(p.authorid != null && p.authorid !== '' ? { authorid: String(p.authorid) } : {}),
    ...(p.autoStartDateUnix != null ? { auto_start_date: p.autoStartDateUnix } : {}),
    ...(p.note ? { note: p.note } : {}),
  });
}

export async function updateTimeEntry(p: { entryId: string | number; timeSeconds: number }): Promise<void> {
  await ws('programinteractiontime', 'update', {
    programid: getProgramId(),
    programinteractiontimeid: String(p.entryId),
    time: p.timeSeconds,
  });
}
